import { createContext, useContext } from "react";

/**
 * Live console state shared by every page: the WebSocket stream, polled
 * platform status, and the actions that change it (block, release, open
 * a profile or a detection).
 */
export const ConsoleContext = createContext(null);
export const useConsole = () => useContext(ConsoleContext);

// Sub-views of each page: [route id, label]. The URL is #/page/tab.
export const TABS = {
  detections: [
    ["feed", "Live feed"],
    ["alerts", "Alerts"],
  ],
  investigations: [
    ["campaigns", "Campaigns"],
    ["hunt", "Threat hunt"],
    ["anomalies", "Anomalies"],
    ["indicators", "Indicators"],
    ["ai", "AI cases"],
  ],
  response: [
    ["sources", "Blocked sources"],
    ["accounts", "Blocked accounts"],
    ["agent", "Response agent"],
  ],
  analytics: [
    ["trends", "Trends"],
    ["attack", "Attack surface"],
    ["intel", "Threat intel feeds"],
  ],
  models: [
    ["ml", "Detection models"],
    ["dl", "Deep learning"],
  ],
  risk: [
    ["risk", "Risk index"],
    ["compliance", "Compliance"],
    ["platform", "Platform security"],
  ],
};

/** Resolve the active tab of a page from the route, falling back to the first. */
export function activeTab(page, tab) {
  const list = TABS[page] || [];
  return list.some(([id]) => id === tab) ? tab : list[0]?.[0];
}

// Kill-chain stage of each attack type (mirrors the backend's _KILL_CHAIN_MAP)
export const KILL_CHAIN = [
  "Reconnaissance",
  "Initial Access",
  "Exploitation",
  "Privilege Escalation",
  "Lateral Movement",
  "Command & Control",
  "Exfiltration",
  "Impact",
];
export const STAGE_OF = {
  port_scan: "Reconnaissance",
  web_attack: "Initial Access",
  brute_force_attempt: "Initial Access",
  brute_force: "Initial Access",
  sql_injection: "Exploitation",
  xss: "Exploitation",
  privilege_escalation: "Privilege Escalation",
  lateral_movement: "Lateral Movement",
  malware_c2: "Command & Control",
  data_exfiltration: "Exfiltration",
  ransomware: "Impact",
  ddos: "Impact",
};

// Why a source was blocked (reason codes set by the backend response policy)
export const BLOCK_REASONS = {
  auto: { label: "Automatic", detail: "High or critical detection" },
  repeat: { label: "Repeat offender", detail: "4+ detections in 10 minutes" },
  manual: { label: "Analyst", detail: "Blocked from a source profile" },
  honeypot: { label: "Honeypot", detail: "Touched the decoy admin page" },
};
export const blockReason = (r) => BLOCK_REASONS[r] || { label: r ? String(r).replace(/_/g, " ") : "Unknown", detail: "" };
